import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FileText, Plus, Search, Download, Eye, Edit, Trash2, 
  Upload, X, Clock, Home, ChevronRight, LogOut, User,
  AlertTriangle, History, RefreshCw, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge variants
const statusVariants = {
  'Active': 'bg-green-500/10 text-green-400 border-green-500/20',
  'Draft': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  'Under Review': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Superseded': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  'Archived': 'bg-slate-800 text-slate-400 border-slate-700',
  'Obsolete': 'bg-red-500/10 text-red-400 border-red-500/20'
};

// Category colors
const categoryColors = {
  'Quality': 'text-purple-400',
  'HR': 'text-blue-400',
  'Safety': 'text-red-400',
  'Operations': 'text-green-400',
  'IT': 'text-cyan-400',
  'Finance': 'text-yellow-400',
  'Environmental': 'text-emerald-400',
  'Maintenance': 'text-orange-400',
  'Procurement': 'text-pink-400',
  'Customer Service': 'text-indigo-400',
  'General': 'text-slate-400'
};

const PolicyManager = ({ onClose }) => {
  const { isAdmin, user, logout } = useAuth();
  const fileInputRef = useRef(null);
  const revisionFileInputRef = useRef(null);
  
  // State
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState({ categories: [], statuses: [], departments: [] });
  const [stats, setStats] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  
  // Selected policy
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    title: '',
    policy_number: '',
    category: 'General',
    department: 'All Departments',
    description: '',
    scope: '',
    owner: '',
    approver: '',
    effective_date: '',
    review_date: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Revision form
  const [revisionForm, setRevisionForm] = useState({
    revision_number: '',
    change_summary: '',
    effective_date: ''
  });
  const [revisionFile, setRevisionFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filterCategory, filterStatus, showArchived]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, policiesRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/policies/config`),
        axios.get(`${BACKEND_URL}/api/policies`, {
          params: {
            category: filterCategory || undefined,
            status: filterStatus || undefined,
            include_archived: showArchived
          }
        }),
        axios.get(`${BACKEND_URL}/api/policies/stats`)
      ]);
      
      setConfig(configRes.data);
      setPolicies(policiesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a PDF file');
      return;
    }
    if (!uploadForm.title.trim()) {
      toast.error('Please enter a policy title');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      Object.entries(uploadForm).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('uploaded_by', 'Admin');

      await axios.post(`${BACKEND_URL}/api/policies/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Policy uploaded successfully');
      setShowUploadDialog(false);
      resetUploadForm();
      fetchData();
    } catch (error) {
      console.error('Error uploading policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload policy');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadRevision = async () => {
    if (!revisionFile) {
      toast.error('Please select a PDF file');
      return;
    }
    if (!revisionForm.revision_number.trim()) {
      toast.error('Please enter a revision number');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', revisionFile);
      formData.append('revision_number', revisionForm.revision_number);
      formData.append('change_summary', revisionForm.change_summary);
      formData.append('effective_date', revisionForm.effective_date);
      formData.append('uploaded_by', 'Admin');

      await axios.post(`${BACKEND_URL}/api/policies/${selectedPolicy.policy_id}/revision`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('New revision uploaded successfully');
      setShowRevisionDialog(false);
      setRevisionForm({ revision_number: '', change_summary: '', effective_date: '' });
      setRevisionFile(null);
      fetchData();
    } catch (error) {
      console.error('Error uploading revision:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload revision');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdatePolicy = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/policies/${selectedPolicy.policy_id}?updated_by=Admin`, {
        title: selectedPolicy.title,
        policy_number: selectedPolicy.policy_number,
        category: selectedPolicy.category,
        department: selectedPolicy.department,
        description: selectedPolicy.description,
        scope: selectedPolicy.scope,
        owner: selectedPolicy.owner,
        review_date: selectedPolicy.review_date
      });

      toast.success('Policy updated successfully');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error updating policy:', error);
      toast.error('Failed to update policy');
    }
  };

  const handleDeletePolicy = async (policy) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${policy.title}"? This cannot be undone.`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/policies/${policy.policy_id}/permanent`);
      toast.success('Policy permanently deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting policy:', error);
      toast.error('Failed to delete policy');
    }
  };

  const handleDownload = async (policy) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/policies/${policy.policy_id}/download?downloaded_by=User`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${policy.title}_v${policy.current_revision}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${policy.title}`);
    } catch (error) {
      console.error('Error downloading policy:', error);
      toast.error('Failed to download policy');
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      title: '',
      policy_number: '',
      category: 'General',
      department: 'All Departments',
      description: '',
      scope: '',
      owner: '',
      approver: '',
      effective_date: '',
      review_date: ''
    });
    setUploadFile(null);
  };

  // Filter policies
  const filteredPolicies = policies.filter(policy => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      policy.title?.toLowerCase().includes(query) ||
      policy.policy_number?.toLowerCase().includes(query) ||
      policy.policy_id?.toLowerCase().includes(query) ||
      policy.description?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-y-auto text-white">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-policy" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(6, 182, 212, 0.15)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(6, 182, 212, 0.15)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-policy)" />
        </svg>
      </div>

      {/* Radial Glow */}
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(6, 182, 212, 0.08) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-cyan-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onClose} className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide">Company Policies</h1>
                <p className="text-xs text-slate-500">ISO9001:2015 Document Control</p>
              </div>
            </div>
          </div>

          {/* Breadcrumbs */}
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-cyan-400 font-medium">Policies</span>
          </nav>

          {/* User & Actions */}
          <div className="flex items-center gap-4">
            {isAdmin() && (
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50 hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] backdrop-blur-sm"
                data-testid="upload-policy-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload Policy
              </Button>
            )}
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <div className="w-8 h-8 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
                <User className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="text-slate-400">{user?.name || user?.email?.split('@')[0]}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1600px] mx-auto">
          
          {/* Stats Bar */}
          {stats && (
            <div className="bg-slate-900/60 backdrop-blur-sm border border-cyan-500/10 rounded-lg px-6 py-4 mb-6">
              <div className="flex flex-wrap items-center gap-8">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Total Policies:</span>
                  <span className="font-bold font-mono text-white">{stats.total}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Active:</span>
                  <span className="font-bold font-mono text-green-400">{stats.active}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Under Review:</span>
                  <span className="font-bold font-mono text-blue-400">{stats.under_review}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">Due for Review:</span>
                  <span className="font-bold font-mono text-orange-400">{stats.due_for_review}</span>
                </div>
              </div>
            </div>
          )}

          {/* Filter Bar */}
          <div className="bg-slate-900/40 backdrop-blur-sm border border-cyan-500/10 rounded-lg px-4 py-3 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[280px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search policies..."
                    className="pl-10 bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                    data-testid="search-policies"
                  />
                </div>
              </div>
              
              <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[160px] bg-slate-950/50 border-slate-800">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {config.categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[140px] bg-slate-950/50 border-slate-800">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {config.statuses.map(status => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                onClick={() => setShowArchived(!showArchived)}
                className={`border-slate-700 hover:border-cyan-500/50 ${showArchived ? 'bg-cyan-500/10 border-cyan-500/30' : ''}`}
              >
                {showArchived ? 'Hide Archived' : 'Show Archived'}
              </Button>
              
              <Button variant="ghost" onClick={fetchData} className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Policy Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            </div>
          ) : filteredPolicies.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <div className="w-20 h-20 rounded-full bg-slate-900/60 border border-slate-800 flex items-center justify-center mb-6">
                <FileText className="w-10 h-10 text-slate-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-400 mb-2">No policies found</h3>
              <p className="text-sm text-slate-500 mb-6">Upload your first policy to get started</p>
              {isAdmin() && (
                <Button onClick={() => setShowUploadDialog(true)} className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50">
                  <Plus className="w-4 h-4 mr-2" /> Upload First Policy
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPolicies.map((policy) => (
                <div
                  key={policy.policy_id}
                  className="group bg-slate-900/40 backdrop-blur-md border border-cyan-500/10 hover:border-cyan-400/50 rounded-xl p-5 transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.1)]"
                  data-testid={`policy-card-${policy.policy_id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate group-hover:text-cyan-400 transition-colors">
                        {policy.title}
                      </h3>
                      <p className="text-xs text-slate-500 font-mono mt-1">
                        {policy.policy_id}
                        {policy.policy_number && ` • ${policy.policy_number}`}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded border font-bold uppercase ${statusVariants[policy.status] || statusVariants['Active']}`}>
                      {policy.status}
                    </span>
                  </div>

                  {/* Category & Revision */}
                  <div className="flex items-center gap-3 mb-3 text-sm">
                    <span className={`${categoryColors[policy.category] || 'text-slate-400'}`}>
                      {policy.category}
                    </span>
                    <span className="text-slate-600">•</span>
                    <span className="text-slate-400 font-mono">Rev {policy.current_revision}</span>
                  </div>

                  {/* Description */}
                  {policy.description && (
                    <p className="text-sm text-slate-400 line-clamp-2 mb-3">{policy.description}</p>
                  )}

                  {/* Dates */}
                  <div className="text-xs text-slate-500 mb-4 space-y-1">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      <span>Effective: {formatDate(policy.effective_date)}</span>
                    </div>
                    {policy.review_date && (
                      <div className="flex items-center gap-2 text-orange-400">
                        <AlertTriangle className="w-3 h-3" />
                        <span>Review: {formatDate(policy.review_date)}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-3 border-t border-cyan-500/10">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedPolicy(policy); setShowViewDialog(true); }}
                      className="flex-1 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/50"
                      data-testid={`view-policy-${policy.policy_id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownload(policy)} className="border-slate-700 hover:border-slate-600 hover:bg-slate-800/50">
                      <Download className="w-4 h-4" />
                    </Button>
                    {isAdmin() && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => { setSelectedPolicy(policy); setShowHistoryDialog(true); }} className="border-slate-700 hover:border-slate-600 hover:bg-slate-800/50">
                          <History className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setSelectedPolicy({...policy}); setShowEditDialog(true); }} className="border-slate-700 hover:border-slate-600 hover:bg-slate-800/50">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeletePolicy(policy)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Upload className="w-5 h-5 text-cyan-400" />
              Upload New Policy
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Upload a PDF document and fill in the policy details.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* File Upload */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-700 hover:border-cyan-500/50 rounded-lg p-8 text-center cursor-pointer transition-colors"
            >
              <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={(e) => setUploadFile(e.target.files[0])} />
              {uploadFile ? (
                <div className="flex items-center justify-center gap-2 text-cyan-400">
                  <FileText className="w-6 h-6" />
                  <span>{uploadFile.name}</span>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setUploadFile(null); }} className="text-red-400 hover:text-red-300">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="w-10 h-10 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400">Click to select PDF file</p>
                  <p className="text-xs text-slate-500 mt-1">PDF files only</p>
                </div>
              )}
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Policy Title *</Label>
                <Input value={uploadForm.title} onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})} className="bg-slate-950/50 border-slate-800" placeholder="e.g., Quality Management Policy" />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Policy Number</Label>
                <Input value={uploadForm.policy_number} onChange={(e) => setUploadForm({...uploadForm, policy_number: e.target.value})} className="bg-slate-950/50 border-slate-800" placeholder="e.g., QMS-001" />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Category</Label>
                <Select value={uploadForm.category} onValueChange={(v) => setUploadForm({...uploadForm, category: v})}>
                  <SelectTrigger className="bg-slate-950/50 border-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {config.categories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Department</Label>
                <Select value={uploadForm.department} onValueChange={(v) => setUploadForm({...uploadForm, department: v})}>
                  <SelectTrigger className="bg-slate-950/50 border-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {config.departments.map(dept => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Policy Owner</Label>
                <Input value={uploadForm.owner} onChange={(e) => setUploadForm({...uploadForm, owner: e.target.value})} className="bg-slate-950/50 border-slate-800" placeholder="e.g., John Smith" />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Effective Date</Label>
                <Input type="date" value={uploadForm.effective_date} onChange={(e) => setUploadForm({...uploadForm, effective_date: e.target.value})} className="bg-slate-950/50 border-slate-800" />
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Next Review Date</Label>
                <Input type="date" value={uploadForm.review_date} onChange={(e) => setUploadForm({...uploadForm, review_date: e.target.value})} className="bg-slate-950/50 border-slate-800" />
              </div>
              
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Description</Label>
                <Textarea value={uploadForm.description} onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})} className="bg-slate-950/50 border-slate-800" rows={2} placeholder="Brief description of this policy..." />
              </div>
              
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Scope</Label>
                <Textarea value={uploadForm.scope} onChange={(e) => setUploadForm({...uploadForm, scope: e.target.value})} className="bg-slate-950/50 border-slate-800" rows={2} placeholder="Who/what does this policy apply to..." />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-800">
              <Button variant="outline" onClick={() => { setShowUploadDialog(false); resetUploadForm(); }} className="border-slate-700">Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading} className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Policy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Policy Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-5xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-400" />
                {selectedPolicy?.title}
                <span className="text-sm text-slate-400 font-normal font-mono">v{selectedPolicy?.current_revision}</span>
              </span>
              <Button variant="outline" size="sm" onClick={() => selectedPolicy && handleDownload(selectedPolicy)} className="border-cyan-500/50 text-cyan-400">
                <Download className="w-4 h-4 mr-1" /> Download
              </Button>
            </DialogTitle>
            <DialogDescription className="sr-only">View policy document</DialogDescription>
          </DialogHeader>
          <div className="flex-1 bg-slate-900 rounded-lg overflow-hidden">
            {selectedPolicy && (
              <iframe src={`${BACKEND_URL}/api/policies/${selectedPolicy.policy_id}/view`} className="w-full h-full border-0" title={selectedPolicy.title} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-400" /> Edit Policy
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Update policy details and metadata.</DialogDescription>
          </DialogHeader>

          {selectedPolicy && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Policy Title</Label>
                  <Input value={selectedPolicy.title} onChange={(e) => setSelectedPolicy({...selectedPolicy, title: e.target.value})} className="bg-slate-950/50 border-slate-800" />
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Policy Number</Label>
                  <Input value={selectedPolicy.policy_number || ''} onChange={(e) => setSelectedPolicy({...selectedPolicy, policy_number: e.target.value})} className="bg-slate-950/50 border-slate-800" />
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Category</Label>
                  <Select value={selectedPolicy.category} onValueChange={(v) => setSelectedPolicy({...selectedPolicy, category: v})}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {config.categories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Department</Label>
                  <Select value={selectedPolicy.department || 'All Departments'} onValueChange={(v) => setSelectedPolicy({...selectedPolicy, department: v})}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {config.departments.map(dept => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Owner</Label>
                  <Input value={selectedPolicy.owner || ''} onChange={(e) => setSelectedPolicy({...selectedPolicy, owner: e.target.value})} className="bg-slate-950/50 border-slate-800" />
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Next Review Date</Label>
                  <Input type="date" value={selectedPolicy.review_date || ''} onChange={(e) => setSelectedPolicy({...selectedPolicy, review_date: e.target.value})} className="bg-slate-950/50 border-slate-800" />
                </div>
                
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Status</Label>
                  <Select value={selectedPolicy.status} onValueChange={(v) => setSelectedPolicy({...selectedPolicy, status: v})}>
                    <SelectTrigger className="bg-slate-950/50 border-slate-800"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {config.statuses.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Description</Label>
                  <Textarea value={selectedPolicy.description || ''} onChange={(e) => setSelectedPolicy({...selectedPolicy, description: e.target.value})} className="bg-slate-950/50 border-slate-800" rows={2} />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-800">
                <Button variant="outline" onClick={() => { const nextRev = parseFloat(selectedPolicy.current_revision) + 0.1; setRevisionForm({ ...revisionForm, revision_number: nextRev.toFixed(1) }); setShowRevisionDialog(true); }} className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                  <Upload className="w-4 h-4 mr-2" /> Upload New Revision
                </Button>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-slate-700">Cancel</Button>
                <Button onClick={handleUpdatePolicy} className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/50">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <History className="w-5 h-5 text-cyan-400" /> Revision History
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">View all previous revisions of this policy.</DialogDescription>
          </DialogHeader>

          {selectedPolicy && (
            <div className="space-y-4 py-4">
              <div className="text-sm text-slate-400 mb-4">
                <span className="text-white font-bold">{selectedPolicy.title}</span>
                <span className="text-slate-500 ml-2 font-mono">({selectedPolicy.policy_id})</span>
              </div>

              {selectedPolicy.revision_history?.length > 0 ? (
                <div className="space-y-3">
                  {[...selectedPolicy.revision_history].reverse().map((rev, idx) => (
                    <div key={rev.revision_id} className={`p-4 rounded-lg border ${idx === 0 ? 'bg-cyan-950/20 border-cyan-500/30' : 'bg-slate-900/50 border-slate-800'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-white font-bold">v{rev.revision_number}</span>
                          {idx === 0 && <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded">Current</span>}
                        </div>
                        <span className="text-xs text-slate-500">{formatDate(rev.uploaded_at)}</span>
                      </div>
                      {rev.change_summary && <p className="text-sm text-slate-400 mb-2">{rev.change_summary}</p>}
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Uploaded by: {rev.uploaded_by}</span>
                        <span>Effective: {formatDate(rev.effective_date)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-8">No revision history available</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revision Dialog */}
      <Dialog open={showRevisionDialog} onOpenChange={setShowRevisionDialog}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-400" /> Upload New Revision
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Upload a new version of the policy document.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div onClick={() => revisionFileInputRef.current?.click()} className="border-2 border-dashed border-slate-700 hover:border-purple-500/50 rounded-lg p-6 text-center cursor-pointer transition-colors">
              <input ref={revisionFileInputRef} type="file" accept=".pdf" className="hidden" onChange={(e) => setRevisionFile(e.target.files[0])} />
              {revisionFile ? (
                <div className="flex items-center justify-center gap-2 text-purple-400">
                  <FileText className="w-5 h-5" />
                  <span className="text-sm">{revisionFile.name}</span>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Select new PDF</p>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Revision Number</Label>
              <Input value={revisionForm.revision_number} onChange={(e) => setRevisionForm({...revisionForm, revision_number: e.target.value})} className="bg-slate-950/50 border-slate-800" placeholder="e.g., 2.0" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Effective Date</Label>
              <Input type="date" value={revisionForm.effective_date} onChange={(e) => setRevisionForm({...revisionForm, effective_date: e.target.value})} className="bg-slate-950/50 border-slate-800" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Change Summary</Label>
              <Textarea value={revisionForm.change_summary} onChange={(e) => setRevisionForm({...revisionForm, change_summary: e.target.value})} className="bg-slate-950/50 border-slate-800" rows={3} placeholder="Describe what changed..." />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowRevisionDialog(false)} className="border-slate-700">Cancel</Button>
              <Button onClick={handleUploadRevision} disabled={uploading} className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/50">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Revision
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PolicyManager;
